import '@rhpt/elements/rhpt-trial-listing/rhpt-trial-listing.js';
import type { RhptTrialListing } from '@rhpt/elements/elements/rhpt-trial-listing/rhpt-trial-listing.js';
import '@rhpt/elements/rhpt-trial-listing/rhpt-trial-listing-lightdom.css';
import '@rhpt/elements/rhpt-trial-row/rhpt-trial-row.js';
import '@rhpt/elements/rhpt-trial-row/rhpt-trial-row-info.js';
import '@rhpt/elements/rhpt-location-show-hide/rhpt-location-show-hide.js';
import '@rhpt/elements/rhpt-dialog/rhpt-dialog.js';
import '@rhpt/elements/rhpt-link/rhpt-link.js';
import '@rhpt/elements/rhpt-login-button/rhpt-login-button.js';
import '@rhpt/elements/rhpt-login-button/rhpt-login-button-lightdom.css';
import '@rhpt/elements/rhpt-sso-login/rhpt-sso-login.js';
import './styles.css';

document.addEventListener('DOMContentLoaded', () => {
  openMobileAccordion();
  attachAnalytics();
  PAYGUpdates();
  fixDialogs();
  rhptdpInsertRow();
});

function openMobileAccordion() {
  if (window.innerWidth < 768) {
    const products = document.querySelector('#products');
    const accordion = products?.shadowRoot?.querySelector('#products');
    if (!accordion) { return; }
    accordion.setAttribute('expanded-index', `${1}`);
  }

  const mediaQuery = window.matchMedia('(max-width: 767px)');
  mediaQuery.addEventListener('change', handleMediaQueryChange);

  function handleMediaQueryChange(e: MediaQueryListEvent) {
    if (e.matches) {
      setTimeout(() => {
        const products = document.querySelector('#products');
        const accordion = products?.shadowRoot?.querySelector('#products');
        if (!accordion) { return; }
        accordion.setAttribute('expanded-index', `${1}`);
      }, 1000);
    }
  }
}

function attachAnalyticsToTopLevelTabPanel() {
  // Find the top level rh-tabs element and add an RH|PTC category to the entire product trial center for analytics.
  const tabPanel = document.querySelector('rh-tabs');
  tabPanel?.setAttribute('data-analytics-category', 'RH|PTC');
}

function attachAnalyticsToListingItemDetailsButton(tabPanel: Element | null) {
  if (!tabPanel) { return; }
  // Go through the listing items and make updates to the buttons for analytics.
  const listingItems = tabPanel.querySelectorAll('div[listing-item]');
  listingItems.forEach(listingItem => {
    // There are three buttons, the "Try it" and "Details" button on the listings and the "Try it" button in the modal.
    // We only need to update two of the buttons, the details button and the try it button in the modal.
    const tryItButton = listingItem.querySelector('a[title="Try it"]');
    const detailsButton = listingItem.querySelector('a[title="Details"]');
    const modalTryItButton = listingItem.querySelector('rh-dialog')?.querySelector('a[title="Try it"]');

    // Grab the trial name from the try it button
    const trialName = tryItButton?.getAttribute('data-analytics-category');

    // Details button format is [Trial Name]|Modal
    detailsButton?.setAttribute('data-analytics-category', `${trialName}|Modal`);
    // Try it button (modal) is Modal|[Trial Name]
    modalTryItButton?.setAttribute('data-analytics-category', `Modal|${trialName}`);
  });
}

function attachAnalyticsToTabPanels() {
  const tabPanels = document.querySelectorAll('rh-tab-panel');

  tabPanels.forEach(tabPanel => {
    const firstTabPanelChildElement = tabPanel.firstElementChild;
    if (firstTabPanelChildElement) {
      const { id } = firstTabPanelChildElement;
      // Adding a data-analytics-category attribute to the tab panel that will be added to any anaytics inside the tab panel.
      // Example values include "featured", "rhel", and "openshift".
      tabPanel.setAttribute('data-analytics-category', id);
      attachAnalyticsToListingItemDetailsButton(tabPanel);
    }
  });
}

function attachAnalytics() {
  attachAnalyticsToTopLevelTabPanel();
  attachAnalyticsToTabPanels();
}

function PAYGUpdates() {
  attachPAYGImages();
  alterPAYGButtonText();
}

function attachPAYGImages() {
  const headers = document.querySelectorAll('.rhptdp-paygo-images #pay-as-you-go-options > rhpt-trial-listing > .rh-card--layout header');
  headers[0].insertAdjacentHTML('beforebegin', '<img class="ROSA" src="/rhdc/managed-files/Logo-Red_Hat-AWS-A-Standard-RGB.svg" alt="Red Hat OpenShift Service on AWS Logo">');
  headers[1].insertAdjacentHTML('beforebegin', '<img class="ARO" src="/rhdc/managed-files/rh-partner-logo-microsoft-azure.svg" alt="Microsoft Azure Red Hat OpenShift Logo">');
}

function alterPAYGButtonText() {
  const buttons = document.querySelectorAll<HTMLAnchorElement>('#pay-as-you-go-options > rhpt-trial-listing a[title="Try it"]');
  buttons.forEach((button: HTMLAnchorElement) => {
    if (button instanceof HTMLAnchorElement && button !== null) {
      button.text = 'Get started';
      button.setAttribute('data-analytics-text', 'Get started');
    }
  });
}

function fixDialogs() {
  fixDialogHeaders();
}

function fixDialogHeaders() {
  const tabs = document.querySelector('rh-tabs');
  const dialogs = document.querySelectorAll('.rhptdp-dialog-headers rh-dialog');
  if (!tabs || !dialogs) { return; }

  dialogs.forEach(dialog => {
    /**
     * Fix nbsp; from being printed as strings in the dialog headers
     */
    const header = dialog.querySelector('[slot="header"]');
    if (header?.textContent) {
      header.innerHTML = header.textContent;
    }
  });
}

/**
 * This is a utitlity attribute that allows you to insert a
 * raw band containing an rhpt-trial-row into the nearest, previous
 * rhpt-trial-listing.
 *
 * Usage: <rhpt-trial-row rhptdp-insert-row="2">
 */
function rhptdpInsertRow() {
  const rows = document.querySelectorAll('[rhptdp-insert-row]');
  const parents = [];

  for (const row of rows) {
    const insertPosition = row.getAttribute('rhptdp-insert-row');
    if (!insertPosition) { return; }

    const parent = getPreviousSiblingBySelector(row, 'rhpt-trial-listing');
    if (!parent) { return; }

    parents.push(parent);

    const parentRows = parent.querySelectorAll(':scope > .rh-card--layout, :scope > rhpt-trial-row');

    // verify that the insertPosition is 1 based indexed
    if (+insertPosition < 1 || Number.isNaN(+insertPosition)) {
      console.warn(`[rhptdp-insert-row] contains an invalid value: ${insertPosition}`);
      continue;
    }

    // if there aren't any previous items
    if (parentRows.length === 0) {
      parent.appendChild(row);
      continue;
    }

    // if we want to add it to the beginning of a list
    if (+insertPosition === 1) {
      // we know that there will be at list one item in parentRows at this point
      parent.insertBefore(row, parentRows[0]);
      continue;
    }

    // if we want to add it to the end of a list
    if (parentRows.length < +insertPosition) {
      parentRows[parentRows.length - 1].after(row);
      continue;
    }
    // if we need to insert it between two rows
    parentRows[+insertPosition - 2].after(row);
  }

  // iterate over list of rows again and forcibley set their [data-rh-background]
  parents.forEach(parent => {
    const parentRows = parent.querySelectorAll(':scope > .rh-card--layout, :scope > rhpt-trial-row');
    parentRows.forEach((row, index) => {
      row.setAttribute('data-rh-background', index % 2 ? 'white' : 'gray');
      row.classList.add('rh-card--layout');
    });
  });

  // tell trial listings that they can now animate
  [...document.querySelectorAll('rhpt-trial-listing')].forEach(async (el: RhptTrialListing) => {
    await customElements.whenDefined('rhpt-trial-listing');
    el.initialize();
  });
}

function getPreviousSiblingBySelector(elem: Element, selector: string) {
  let sibling = elem.previousElementSibling;
  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling;
    }
    sibling = sibling.previousElementSibling;
  }
  return null; // No matching sibling found
}
